









































import mixins from 'vue-typed-mixins'
import { mapActions, mapGetters } from 'vuex'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import AbstractColumnLink from '@/abstract/ColumnLink'
import AbstractColumnValue from '@/abstract/ColumnValue'
import { UserScope } from '@/models/users'
import {
  ScriptAction,
  ScriptStatus,
  ScriptPublishStatus,
  ScriptPublishStatusMessages,
} from '@/constants/scripts'
import { SCRIPTS__MAKE_ACTION } from '@/store/actions'
import { RouteNames } from '@/router'

import type { AxiosResponse, AxiosPromise } from 'axios'
import type { RawLocation } from 'vue-router'
import type { CreateDraftPayload, CreateDraftResponse, ScriptActionPayload } from '@/models/scripts'

interface Data {
  inProgress: boolean
}

interface Methods {
  [SCRIPTS__MAKE_ACTION]: (data: ScriptActionPayload) => AxiosPromise
  [ScriptAction.Draft]: (payload: CreateDraftPayload) => Promise<CreateDraftResponse>
  makeAction: (action: ScriptAction) => Promise<AxiosResponse<{ publish: ScriptPublishStatus } | boolean>>
  publish: () => void
  unPublish: () => void
  createDraft: () => void
  deleteItem: () => void
}

interface Computed {
  canDoScope: (checkingScope: UserScope) => boolean
  draftId?: string
  hasVersionsWithoutDraft: boolean
  isPublished: boolean
  showRoute: RawLocation
  editRoute: RawLocation
  createVersionRoute: RawLocation
  canUseScript: boolean
  canEditScript: boolean
  canPublishScript: boolean
  canDeleteScript: boolean
  userIsGuest: boolean
}

export default mixins(AbstractColumnLink, AbstractColumnValue).extend<Data, Methods, Computed, {}>({
  data() {
    return {
      inProgress: false,
    }
  },
  computed: {
    ...mapGetters(['canDoScope']),
    draftId(): string | undefined {
      return this.init.draftId
    },
    hasVersionsWithoutDraft(): boolean {
      const { versionsCount = 0 } = this.init
      return this.draftId ? versionsCount > 1 : versionsCount > 0
    },
    isPublished(): boolean {
      return this.init.status === ScriptStatus.Publish
    },
    showRoute(): RawLocation {
      return {
        name: RouteNames.ScriptPage,
        params: { scriptId: this.init.id },
      }
    },
    editRoute(): RawLocation {
      return {
        name: RouteNames.ScriptSchemeEdit,
        params: {
          scriptId: this.init.id,
          instanceId: this.init.draftId as string,
        },
      }
    },
    createVersionRoute(): RawLocation {
      return {
        name: RouteNames.VersionCreate,
        params: { scriptId: this.init.id },
      }
    },
    canUseScript(): boolean {
      return this.canDoScope(UserScope.ScriptsUse)
    },
    canEditScript(): boolean {
      return this.canDoScope(UserScope.ScriptsEdit)
    },
    canPublishScript(): boolean {
      return this.canDoScope(UserScope.ScriptsPublish)
    },
    canDeleteScript(): boolean {
      return this.canDoScope(UserScope.ScriptsDelete)
    },
    userIsGuest(): boolean {
      return (
        !this.canUseScript &&
        !this.canEditScript &&
        !this.canPublishScript &&
        !this.canDeleteScript
      )
    },
  },
  methods: {
    ...mapActions([SCRIPTS__MAKE_ACTION, ScriptAction.Draft]),
    makeAction(
      action: ScriptAction,
    ): AxiosPromise<{ publish: ScriptPublishStatus } | boolean> {
      this.inProgress = true

      return this[SCRIPTS__MAKE_ACTION]({
        action,
        id: this.init.id,
        instance: this.init.instanceId,
      })
        .then(response => {
          this.inProgress = false
          return response
        })
        .catch(err => {
          this.inProgress = false
          throw err
        })
    },
    publish(): void {
      if (this.inProgress) {
        return
      }

      this.makeAction(ScriptAction.Publish).then(response => {
        let type = 'info'
        let text = ''
        if (
          typeof response.data !== 'boolean' &&
          typeof response.data.publish === 'number'
        ) {
          text = ScriptPublishStatusMessages[response.data.publish]
          if (
            [
              ScriptPublishStatus.Not,
              ScriptPublishStatus.NotFound,
              ScriptPublishStatus.NotVersioned,
            ].includes(response.data.publish)
          ) {
            type = 'warning'
          }
        }
        this.$store.dispatch(ADD_TOAST_MESSAGE, {
          text,
          type,
        })
      })
    },
    createDraft() {
      if (this.inProgress) {
        return
      }

      const payload: CreateDraftPayload = {
        id: this.init.id,
        instance: this.init.instanceId,
      }
      this[ScriptAction.Draft](payload).then(
        (response: CreateDraftResponse) => {
          const { id } = this.init
          this.$router.push({
            name: RouteNames.ScriptSchemeEdit,
            params: { scriptId: id, instanceId: response.id },
          })
        },
      )
    },
    unPublish(): void {
      if (this.inProgress) {
        return
      }

      this.$useConfirmDialog(this.$tc('script.actions.unpublishConfirm'), () => {
        this.makeAction(ScriptAction.UnPublish).then(response => {
          if (response.data) {
            this.$store.dispatch(ADD_TOAST_MESSAGE, {
              text: this.$tc('script.actions.unpublishSuccess'),
              type: 'info',
            })
          }
        })
      })
    },
    deleteItem(): void {
      if (this.inProgress) {
        return
      }

      this.$useConfirmDialog([
        this.$tc('script.actions.removeConfirm'),
        {
          type: 'danger',
          okText: this.$tc('common.remove'),
          cancelText: this.$tc('common.cancel'),
        },
      ], () => {
        this.makeAction(ScriptAction.DeleteItem).then(response => {
          if (response.data) {
            this.$store.dispatch(ADD_TOAST_MESSAGE, {
              text: this.$tc('script.actions.removeSuccess'),
              type: 'info',
            })
          }
        })
      })
    },
  },
})
